import { computed, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { Well } from '#models';

export interface OpenCaseBarItem {
  id: string;
  label: string;
  route: string;
  queryParams: Record<string, string> | null;
  activeNavPath: string;
  caseId: string;
  well: Well;
}

@Injectable({
  providedIn: 'root',
})
export class OpenCaseBarService {
  private _cases = signal<OpenCaseBarItem[]>([]);
  private _activeCaseId = signal<string | null>(null);
  private _wellSearchSelection$ = new Subject<Well>();

  cases = this._cases.asReadonly();
  activeCaseId = this._activeCaseId.asReadonly();
  hasCases = computed(() => this._cases().length > 0);
  activeCase = computed(
    () =>
      this._cases().find((caseItem) => caseItem.id === this._activeCaseId()) ??
      null,
  );

  /** Emits synchronously when a well is selected from the global header search. */
  wellSearchSelection$ = this._wellSearchSelection$.asObservable();

  notifyWellSelectedFromSearch(well: Well): void {
    this._wellSearchSelection$.next(well);
  }

  upsertWellCase(
    well: Well,
    route = '/create-review',
    caseId = '',
    activeNavPath?: string,
    queryParams: Record<string, string> | null = null,
  ): void {
    // DISABLED: return to re-enable case bar state management
    return;

    const id = `well-${well.id}`;

    this._cases.update((currentCases) => {
      const existingIndex = currentCases.findIndex(
        (caseItem) => caseItem.id === id,
      );
      const resolvedActiveNavPath =
        activeNavPath ?? currentCases[existingIndex]?.activeNavPath ?? route;
      const nextCase = {
        id,
        label: well.wellName,
        route,
        queryParams,
        activeNavPath: resolvedActiveNavPath,
        caseId,
        // Preserve the existing well metadata when updating, but allow the name
        // to refresh after a case page resolves the real well name from the API.
        well: existingIndex !== -1
          ? { ...currentCases[existingIndex].well, wellName: well.wellName }
          : well,
      };

      if (existingIndex === -1) {
        return [...currentCases, nextCase];
      }

      return currentCases.map((caseItem) =>
        caseItem.id === id ? nextCase : caseItem,
      );
    });

    this._activeCaseId.set(id);
  }

  activateCase(caseId: string): void {
    if (this._cases().some((caseItem) => caseItem.id === caseId)) {
      this._activeCaseId.set(caseId);
    }
  }

  clearActiveCase(): void {
    this._activeCaseId.set(null);
  }

  closeCase(caseId: string): void {
    const currentCases = this._cases();
    const closedCaseIndex = currentCases.findIndex(
      (caseItem) => caseItem.id === caseId,
    );

    if (closedCaseIndex === -1) {
      return;
    }

    const remainingCases = currentCases.filter(
      (caseItem) => caseItem.id !== caseId,
    );

    this._cases.set(remainingCases);

    if (this._activeCaseId() !== caseId) {
      return;
    }

    const fallbackCase =
      remainingCases[Math.max(0, closedCaseIndex - 1)] ??
      remainingCases[0] ??
      null;

    this._activeCaseId.set(fallbackCase?.id ?? null);
  }

  clearCases(): void {
    this._cases.set([]);
    this._activeCaseId.set(null);
  }
}
